import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRightIcon } from "@/shared/assets/icons";
import { FileUploader } from "@/shared/components";
import { Button, Prompt } from "@/shared/ui";
import { useGetContractStatusQuery, useUploadContractMutation } from "./api";

interface Props {
  token: string;
  onNext: () => void;
}

export default function ContractForm({ token, onNext }: Props) {
  const { t } = useTranslation("ApplyApplicationPage");

  const { data: contractStatus } = useGetContractStatusQuery(token);
  const [uploadContract, uploadState] = useUploadContractMutation();

  const [contractFile, setContractFile] = useState<File | null>(null);
  const [hideExisting, setHideExisting] = useState(false);

  const [uploadPrompt, setUploadPrompt] = useState<{
    variant: "success" | "error";
    title: string;
    text: string;
  } | null>(null);

  async function handleFileChange(file: File | null) {
    if (!file) {
      setContractFile(null);
      return;
    }
    if (uploadState.isLoading) return;
    setContractFile(file);
    try {
      await uploadContract({ token, file }).unwrap();
      setUploadPrompt({
        variant: "success",
        title: t("contract.prompt.success_title"),
        text: t("contract.prompt.success_text"),
      });
      setContractFile(null);
      setHideExisting(false);
    } catch {
      setUploadPrompt({
        variant: "error",
        title: t("contract.prompt.error_title"),
        text: t("contract.prompt.error_text"),
      });
      setContractFile(null);
    }
  }

  return (
    <div className="flex flex-col justify-between min-h-[640px] gap-7">
      <div className="flex flex-col gap-7">
        <h2 className="text-display-2xs content-base-primary">{t("contract.title")}</h2>

        <FileUploader
          label={t("contract.label")}
          value={contractFile}
          existingFileUrl={hideExisting ? null : (contractStatus?.bessrochnyi_contract_file_url ?? null)}
          onChange={handleFileChange}
          onRemoveExisting={() => {
            setHideExisting(true);
            setContractFile(null);
          }}
          accept=".pdf"
          maxSizeMB={50}
          formatsText={t("contract.formats", { max: 50 })}
        />
      </div>

      <div className="flex justify-end pt-4">
        <Button variant="primary" size="lg" onClick={onNext}>
          {t("contract.next_section")}
          <span className="text-white">
            <ArrowRightIcon />
          </span>
        </Button>
      </div>

      {uploadState.isLoading && (
        <Prompt
          loaderMode
          onClose={() => {}}
          title=""
          text=""
          loaderText={t("contract.prompt.loading")}
          additionalText={t("contract.prompt.loading_text")}
          namespace="ApplyApplicationPage"
        />
      )}

      {uploadPrompt && (
        <Prompt
          title={uploadPrompt.title}
          text={uploadPrompt.text}
          variant={uploadPrompt.variant}
          onClose={() => setUploadPrompt(null)}
          namespace="ApplyApplicationPage"
        />
      )}
    </div>
  );
}
